/**
 * adminTranslations.js
 *
 * Dedicated 4-language dictionary for the Admin Space (fr / en / es / zh).
 * Rebuilt in v50.5 after `translations.js` lost its `adminI18n` block during
 * a file truncation. Kept in a **separate module** so accidental encoding
 * issues in the massive `translations.js` never take down the admin UI again.
 *
 * All keys used across the admin surface are listed in FR (source of truth),
 * then translated to en / es / zh. When a key is missing in a target language
 * the `at()` helper falls back to English then to the raw key.
 */

// --- French — source of truth ---------------------------------------------
const fr = {
  // Global
  exit: 'Retour',
  cancel: 'Annuler',
  copy: 'Copier',
  copiedKey: 'Clé copiée',
  confirmRevoke: 'Révoquer cette clé ?',
  keyOnceBanner: "Cette clé n'est visible qu'une seule fois — copiez-la maintenant.",

  // Dashboard header
  dashboardTitle: 'Tableau de bord admin',
  dashboardSubtitle: 'Vue d\'ensemble de Destine',

  // Sections
  hideSection: 'Masquer la section',
  showApisUsed: 'Voir les API & services utilisés',
  iaArchitectureSection: 'Destine IA',
  tabApia: 'AP-IA',
  teamSectionTitle: 'Équipe Destine',
  inactiveUsersSection: 'Utilisateurs inactifs',
  inactiveUsersTitle: 'Utilisateurs inactifs (60+ jours)',
  securityTitle: 'Sécurité',
  securitySubtitle: 'Alertes et score de sécurité',
  securityScore: 'Score de sécurité',
  securityAlerts: 'Alertes actives',
  securityNoAlerts: 'Aucune alerte en cours.',
  securityAdvice: 'Conseils de sécurité',
  regionalTitle: 'Langues régionales (Guyane)',
  africaTitle: 'GPT Africa — Langues régionales (Afrique)',
  regionalSubtitle: 'Corpus de traductions communautaires',
  regionalCount: 'contributions',
  regionalPerLang: 'Par langue',
  regionalAdd: 'Ajouter une traduction',
  regionalAddTitle: 'Nouvelle traduction régionale',
  regionalCorrections: 'Corrections proposées',
  regionalCorrectionsShort: 'Corrections',
  regionalExport: 'Exporter (CSV)',
  regionalFormSrcPh: 'Phrase source (fr / en / nl)',
  regionalFormTgtPh: 'Traduction régionale',
  regionalFormOk: 'Confirmée',
  regionalFormMissing: 'Manquante',
  regionalFormCorrection: 'Correction',
  regionalFormSave: 'Enregistrer',
  regionalFormSaving: 'Enregistrement…',

  // PIN admin lock
  pinTitle: 'Accès protégé',
  pinShow: 'Afficher',
  pinHide: 'Masquer',
  pinEdit: 'Modifier le PIN',
  pinOthers: 'PIN admin standard',
  pinSuper: 'PIN super admin',

  // Leaderboards
  leaderboardTitle: 'Classements',
  lbFollowers: 'Abonnés',
  lbLikes: 'J\'aime',
  lbPosts: 'Publications',
  lbComments: 'Commentaires',
  lbShares: 'Partages',
  lbReferrers: 'Parrainages',
  lbLoading: 'Chargement…',
  lbEmpty: 'Aucune donnée',

  // Tiles — Actions rapides (dashboard cards)
  tileUpdateNotifTitle: 'Notifier une mise à jour',
  tileUpdateNotifDesc: 'Envoyer une notification à tous les utilisateurs',
  tileWeeklyCinemaTitle: 'Cinéma de la semaine',
  tileWeeklyCinemaDesc: 'Publier la sélection ciné hebdomadaire',
  tileUsersTitle: 'Utilisateurs',
  tileUsersDesc: 'Gérer les comptes',
  tileBadgeTitle: 'Badges vérifiés',
  tileBadgeDesc: 'Attribuer/retirer le badge bleu',
  tileBadgeCandidatesTitle: 'Candidatures badge',
  tileBadgeCandidatesDesc: 'Examiner les demandes de vérification',
  tileMyIaInsightsTitle: 'Insights Déesse',
  tileMyIaInsightsDesc: 'Statistiques d\'usage de l\'assistant',
  tileApiIntegrationsTitle: 'Intégrations API',
  tileApiIntegrationsDesc: 'Statut des services externes',
  tileModerationTitle: 'Modération',
  tileModerationDesc: 'Signalements et alertes IA',
  tileCinemaTitle: 'Cinéma',
  tileCinemaDesc: 'Films, actualités, sorties',
  tileFranchisesTitle: 'Franchises',
  tileFranchisesDesc: 'Studios & sagas',
  tileTmdbTitle: 'Sync TMDB',
  tileTmdbDesc: 'Synchroniser sorties & box-office',
  tileFreeFilmTitle: 'Films gratuits',
  tileFreeFilmDesc: 'Catalogue à visionner',
  tileVisibilityTitle: 'Visibilité des sections',
  tileVisibilityDesc: 'Masquer / réafficher Accueil, MyMouv, MyJob, Films libres',
  filmsLibresTitle: 'Films libre de droit — visibilité',
  filmsLibresDesc: "Cette section n'est visible que par les utilisateurs francophones. Vous pouvez la masquer à tous en un clic.",
  filmsLibresVisible: 'Visible',
  filmsLibresHidden: 'Masquée',
  filmsLibresOk: 'Section visible',
  filmsLibresKo: 'Section masquée',
  filmsLibresError: 'Erreur',
  filmsPlaybackTitle: 'Films libre de droit — mode de lecture',
  filmsPlaybackDesc: "Choisissez comment les utilisateurs regardent les films YouTube : lecteur intégré directement dans l'app, ou navigateur web.",
  filmsPlaybackInApp: "Dans l'app",
  filmsPlaybackBrowser: 'Navigateur web',
  filmsPlaybackInAppOk: "Lecture dans l'app activée",
  filmsPlaybackBrowserOk: 'Lecture via navigateur activée',
  giaAdminTitle: 'Green IA — protections & certifications',
  giaAdminProtected: 'Individus protégés',
  giaAdminApiaReqs: 'Demandes Green AP-IA',
  giaAdminReqSubtitle: 'Demandes de certificat Green AP-IA',
  giaAdminApprove: 'Approuver',
  giaAdminReject: 'Refuser',
  giaAdminCert: 'Certificat',
  giaAdminIssued: 'Certificat émis',
  giaAdminRefused: 'Refusé',
  giaAdminError: 'Erreur',
  subAdminTitle: 'Abonnements payants',
  subAdminEnabled: 'Activés',
  subAdminDisabled: 'Masqués',
  subAdminOnMsg: 'Abonnements activés',
  subAdminOffMsg: 'Abonnements masqués',
  subAdminError: 'Erreur',
  subAdminTotal: 'Total',
  subAdminActive: 'Actifs',
  subAdminPending: 'En attente',
  subAdminCancelled: 'Résiliés',
  subAdminRevenue: "Chiffre d'affaires estimé",
  subAdminEmpty: 'Aucun abonnement.',
  subAdminCancelConfirm: 'Résilier cet abonnement ?',
  photosTitle: 'Photos partagées',
  videosTitle: 'Vidéos partagées',
  greenIaSectionTitle: 'Green IA — Registre anti-deepfake',
  giaCertView: 'Voir le certificat (aperçu)',
  giaLabelI18nNote: 'Le label est traduit automatiquement dans les 13 langues — il s\'affiche dans la langue du pro au moment de sa demande.',
  ratingsSectionTitle: 'Notes utilisateurs — Sorties & Box-office',
  socialAgentsSectionTitle: 'Agents IA Social Media',
  avsTitle: 'Studio vidéo admin',
  avsSub: 'Vidéos de moins de 5 min — sous-titres personnalisés ou doublage. Gratuit pour les admins.',
  avsTabSubs: 'Sous-titres', avsTabDub: 'Doublage',
  avsPick: 'Choisir une vidéo (5 min max)',
  avsLangSubs: 'Langue des sous-titres', avsLangDub: 'Langue du doublage', avsLangOriginal: 'Langue originale (pas de traduction)',
  avsGenerate: 'Générer les sous-titres', avsGenerating: 'Transcription…',
  avsEditHint: 'Modifiez librement le texte des sous-titres ci-dessous avant l\u2019incrustation.',
  avsBurn: 'Incruster & télécharger', avsBurning: 'Incrustation…',
  avsDub: 'Doubler la vidéo', avsDubbing: 'Doublage…',
  avsJobs: 'Doublages', avsNoJobs: 'Aucun doublage pour le moment', avsDownload: 'Télécharger',
  avsProcessing: 'En cours…', avsDone: 'Terminé', avsFailed: 'Échoué',
  avsTooLong: 'Vidéo trop longue (max 5 min)', avsTooBig: 'Vidéo trop lourde (max 150 MB)',
  avsOk: 'C\u2019est lancé !', avsKo: 'Échec de l\u2019opération',
  avsRedownload: 'Télécharger à nouveau le dernier résultat',
  teamSectionsBtn: 'Sections attribuées', teamSectionsShort: 'sections',
  teamSectionsLabel: 'Sections (Actions rapides) accessibles à cet admin :',
  teamSectionsSave: 'Enregistrer', teamSectionsSaved: 'Sections mises à jour',
  scpTitle: 'Publication auto TikTok & YouTube Shorts',
  scpConnect: 'Connecter', scpConnected: 'Connecté',
  scpRedirectHint: 'Enregistrez ces URI de redirection dans les consoles développeur :',
  scpPick: 'Choisir une vidéo', scpTitlePh: 'Titre / légende', scpDescPh: 'Description (hashtags inclus)',
  scpPublish: 'Publier maintenant', scpPublishing: 'Publication…',
  scpPublished: 'Publié !', scpPartial: 'Publication partielle — vérifiez les résultats', scpKo: 'Échec',
  scpJobs: 'Dernières publications',
  scpAnalyze: 'Agent IA — Analyser mes stats', scpAnalyzing: 'Analyse…',
  scpFollowers: 'Abonnés', scpGain7: 'Gain 7 j', scpGain30: 'Gain 30 j',
  scpAiTitle: 'Analyse de l\u2019agent IA',
  scvTitle: 'Agent créateur de vidéos IA',
  scvSub: 'L\u2019IA écrit le script, génère les visuels, la voix off et les sous-titres, puis ajoute l\u2019outro Destine (logo + téléchargement iOS/Android). Prévisualisez puis publiez.',
  scvGenerate: 'Créer une vidéo', scvGenerating: 'Génération en cours…', scvStarted: 'Génération lancée ! (~2 min)',
  scvTopicWeekly: 'Films de la semaine', scvTopicBox: 'Box-office', scvTopicNews: 'Actus cinéma', scvTopicApp: 'Fonctionnalités Destine IA',
  scvPickPlatform: 'Cochez au moins une plateforme (TikTok/YouTube) ci-dessous',
  scvSchedTitle: 'Publication auto quotidienne (jusqu\u2019à 2 vidéos / jour)',
  scvSchedAutoPub: 'Publier automatiquement sur les plateformes cochées',
  scvSchedSave: 'Enregistrer la planification', scvSchedSaved: 'Planification enregistrée',
  scvSchedHint: 'Heures locales (Guyane, UTC-3). Sujets alternés\u00a0: cinéma / box-office. La vidéo intègre le logo Destine et la musique originale «\u00a0Rêves de Cinéma\u00a0».',
  scvSubClassic: 'Sous-titres — Classique (blanc)', scvSubBold: 'Sous-titres — Gras + ombre', scvSubYellow: 'Sous-titres — Jaune YouTube', scvSubCinema: 'Sous-titres — Cinéma (boîte noire)', scvSubNeon: 'Sous-titres — Néon rose',
  scvMusicPreview: 'Écouter la musique',
  legalDocsTileTitle: 'Documents juridiques', legalDocsTileDesc: 'CGU, CGV, confidentialité, PDF & éditeur',
  edepotsTileTitle: 'e-Dépôts', edepotsTileDesc: 'Attestations de dépôt, téléchargements, dépôt test gratuit (sans paiement)',
  edepotsAdminTitle: 'e-Dépôts — Gestion & test', edepotsCreateFree: 'Créer un dépôt admin (gratuit — sans paiement)',
  edepotsTitle: 'Titre de l\u2019œuvre', edepotsDescription: 'Description (optionnel)', edepotsCreateBtn: 'Créer le dépôt admin gratuit',
  edepotsAll: 'Tous les dépôts', edepotsCert: 'Attestation PDF', edepotsFile: 'Fichier original',
  adsPricingTitle: 'Prix & durées des publicités', adsPricingSub: 'Modifie les prix et durées de diffusion. Les changements sont visibles instantanément côté PRO dans Destine Ads.',
  adsPriceLabel: 'Prix (€)', adsDurationLabel: 'Durée (jours)', adsVideo: 'Publicité vidéo', adsAudio: 'Publicité audio (spot)', adsImage: 'Publicité affiche (image)',
  adsPricingSave: 'Enregistrer', adsPricingSaved: 'Prix mis à jour — visibles immédiatement côté PRO',
  mymouvEventsTile: 'MyMouv — Événements & billetterie',
  adminPitchTitle: 'Studio Pitch (gratuit pour les admins)', adminPitchHint: 'Téléprompteur + rédaction IA + traduction 13 langues + enregistrement vidéo/audio',
  legalDocsTitle: 'Documents juridiques', legalDocsSub: 'Gère les PDF et les contenus juridiques de l\u2019app. Les CGU et CGV sont éditables directement ici.',
  legalPdfPresent: 'PDF présent', legalNoPdf: 'Aucun PDF', legalTextPresent: 'contenu texte OK', legalNoText: 'sans texte', legalEditable: 'Éditable',
  legalReplace: 'Remplacer PDF', legalUpload: 'Uploader PDF', legalDownload: 'Télécharger', legalDeletePdf: 'Supprimer PDF', legalEdit: 'Modifier le contenu',
  legalSave: 'Enregistrer', legalExportPdf: 'Enregistrer & Télécharger le PDF',
  legalMustBePdf: 'Le fichier doit être un PDF', legalUploaded: 'PDF enregistré', legalUploadKo: 'Erreur upload',
  legalConfirmDelete: 'Supprimer le PDF actuel ?', legalDeleted: 'PDF supprimé', legalDeleteKo: 'Erreur suppression',
  legalSaved: 'Contenu enregistré', legalSaveKo: 'Erreur enregistrement', legalPdfReady: 'PDF prêt à télécharger', legalExportKo: 'Erreur export PDF',
  legalMdPlaceholder: 'Contenu Markdown (## Titre, - liste, **gras**)…', legalGenericError: 'Erreur', legalDownloadKo: 'Impossible de télécharger',
  tileEmailTitle: 'Envoyer un email',
  tileEmailDesc: 'Écrire à un utilisateur sur son adresse de compte',
  emailModalTitle: 'Envoyer un email', emailPickUser: 'Rechercher un utilisateur…', emailSubjectPh: "Titre de l'email", emailBodyPh: 'Texte du message…', emailSendBtn: 'Envoyer', emailSentOk: 'Email envoyé à', emailMissingFields: 'Destinataire, titre et texte requis', emailHistory: 'Derniers envois',
  bizTasksSectionTitle: 'Démarches administratives & paiements', bizDueNow: 'À faire ce mois-ci', bizMarkDone: 'Marquer fait', bizConfirmDelete: 'Supprimer cette démarche ?', bizMissing: 'Titre et mois requis', bizAdd: 'Ajouter', bizAddTask: 'Ajouter une démarche', bizTitlePh: 'Titre de la démarche', bizDescPh: 'Description (optionnel)',
  cyberTitle: 'Agent IA Cyberdéfense', cyberProtected: 'Personnes protégées Green IA', cyberAudit: 'Entrées audit chaînées', cyberFailed: 'Connexions échouées (7j)', cyberGreenTitle: 'Comment les données Green IA sont protégées', cyberAnalyze: 'Lancer une analyse IA', cyberVerify: 'Vérifier le registre', cyberChainOk: "Chaîne d'audit intacte", cyberEntries: 'entrées scellées', cyberReportTitle: "Rapport de l'agent IA",
  giaAssetsTitle: 'Label & certificat Green IA',
  giaLabelDl: 'Télécharger le label (SVG)',
  giaCertDl: 'Télécharger le certificat (aperçu PDF)',
  subscriptionsSectionTitle: 'Abonnements Premium',
  photosFlag: 'Signaler',
  photosUnflag: 'Rétablir',
  photosFlagged: 'signalées',
  photosFlaggedBadge: 'Signalée',
  photosEmpty: 'Aucune photo partagée',
  photosConfirmDelete: 'Supprimer ce post photo ?',
  photosDeleted: 'Post photo supprimé',
  vtStatsTotal: 'Vidéos totales',
  vtStatsFlagged: 'Signalées',
  vtStatsLikes: 'Likes cumulés',
  vtStatsViews: 'Vues cumulées',
  saTitle: 'Agents IA Social Media',
  saTabCompose: 'Composer',
  saTabList: 'Programmés',
  saTabMod: 'Modération',
  saTitleField: 'Titre du post *',
  saBodyField: 'Contenu du post *',
  saHashtags: 'Hashtags (ex: #cinema #ia)',
  saMediaUrl: 'URL du média (image/vidéo — optionnel)',
  saSchedule: 'Programmer',
  saMissing: 'Titre, contenu et au moins une plateforme requis',
  saScheduled: 'Post programmé',
  saFlagged: 'Post programmé mais SIGNALÉ (termes interdits détectés)',
  saError: 'Erreur',
  saEmpty: 'Aucun post programmé.',
  saDeleteConfirm: 'Supprimer ce post ?',
  saModTitle: 'Termes interdits pour l\u2019auto-modération',
  saModDesc: 'Tout post contenant l\u2019un de ces termes sera automatiquement signalé et bloqué.',
  saModAdd: 'Ajouter un terme…',
  saModSave: 'Enregistrer',
  saModSaved: 'Configuration enregistrée',
  tileAdsValidationTitle: 'Validation Ads',
  tileAdsValidationDesc: 'Approuver les publicités en attente',
  tileMonetizationTitle: 'Monétisation',
  tileMonetizationDesc: 'Sponsors & offres créateurs',
  tileAdsTitle: 'Publicités',
  tileAdsDesc: 'Campagnes actives',

  // TMDB
  tmdbSyncing: 'Synchronisation…',
  tmdbSyncDone: 'Synchronisation TMDB terminée',
  tmdbSyncError: 'Erreur de synchronisation TMDB',

  // Weekly cinema
  weeklyCinemaFilms: 'Films sélectionnés',
  weeklyCinemaAudience: 'Public cible',
  weeklyCinemaHint: 'Envoyer une notification hebdomadaire avec la sélection cinéma.',
  weeklyCinemaPreview: 'Aperçu',
  weeklyCinemaSend: 'Envoyer',
  weeklyCinemaSending: 'Envoi…',
  weeklyCinemaConfirm: 'Confirmer l\'envoi à tous les utilisateurs ?',

  // AP-IA (developer keys)
  apiaKeysTitle: 'Clés AP-IA',
  apiaTabPending: 'En attente',
  apiaTabApproved: 'Approuvées',
  apiaTabRefused: 'Refusées',
  apiaRequestsEmpty: 'Aucune demande',
  apiaSubmittedOn: 'Soumise le',
  apiaPurpose: 'Objet',
  apiaReason: 'Raison',
  apiaApprove: 'Approuver',
  apiaRefuse: 'Refuser',
  apiaApproved: 'Approuvée',
  apiaRefused: 'Refusée',
  apiaRefusePrompt: 'Motif du refus (optionnel)',
  apiaKeyRevoke: 'Révoquer',
  apiaKeyRevoked: 'Clé révoquée',
  apiaManualTitle: 'Générer une clé manuelle',
  apiaManualHint: 'Créer une clé AP-IA sans passer par la demande utilisateur (usage interne).',
  apiaManualNamePh: 'Nom de la clé',
  apiaManualDescPh: 'Description (optionnelle)',
  apiaManualGenerate: 'Générer la clé',

  // Team management (Destine staff)
  teamTabTeam: 'Équipe',
  teamTabPartners: 'Associés',
  teamEmpty: 'Aucun membre',
  teamPartnersEmpty: 'Aucun associé',
  teamGrant: 'Ajouter un membre',
  teamGrantConfirm: 'Ajouter cet utilisateur à l\'équipe ?',
  teamGranted: 'Membre ajouté à l\'équipe',
  teamConfirmRevoke: 'Retirer ce membre de l\'équipe ?',
  teamRevoked: 'Membre retiré',
  teamJobType: 'Poste',
  teamContract: 'Type de contrat',
  teamHours: 'Heures / semaine',
  teamSalaryEmail: 'Email de contact salaire',
  teamEntryDate: 'Date d\'entrée',
  teamCvUpload: 'Téléverser CV (PDF)',
  teamCvReplace: 'Remplacer le CV',
  teamCvView: 'Voir le CV',
  teamCvUploaded: 'CV téléversé',
  teamPhoto: 'Photo',
  teamPhotoUpload: 'Téléverser une photo',
  teamPhotoUploaded: 'Photo téléversée',
  teamProfileSave: 'Enregistrer le profil',
  teamProfileSaved: 'Profil enregistré',
  teamSlip: 'Fiche de paie',
  teamSlipPdf: 'Fiche de paie (PDF)',
  teamSlipSend: 'Envoyer la fiche',
  teamSlipSent: 'Fiche envoyée',
  teamSlipStored: 'Fiche archivée',
  teamSlipFileRequired: 'Fichier PDF requis',
  teamSlipPeriodInvalid: 'Période invalide (format YYYY-MM)',
  teamPartnerAdd: 'Ajouter un associé',
  teamPartnerAdded: 'Associé ajouté',
  teamPartnerNew: 'Nouvel associé',
  teamPartnerType: 'Type',
  teamPartnerShareholder: 'Actionnaire',
  teamPartnerConfirmDelete: 'Supprimer cet associé ?',

  // Pay slips (new v50.5)
  slipsTab: 'Fiches de paie',
  slipsEmpty: 'Aucune fiche',
  slipsDownload: 'Télécharger',
  slipsDelete: 'Supprimer',
  slipsConfirmDelete: 'Supprimer cette fiche ?',

  // Users-by-language modal (v50.5)
  usersByLangTitle: 'Utilisateurs par langue',
  usersByLangSubtitle: 'Répartition dans les 13 langues supportées',
  usersByLangOther: 'Autre / non renseigné',
  usersByLangTotal: 'Total',
  usersByLangEmpty: 'Aucune donnée',
  usersByLangClickHint: 'Cliquez pour voir la répartition',
};

// --- English --------------------------------------------------------------
const en = {
  exit: 'Back',
  cancel: 'Cancel',
  copy: 'Copy',
  copiedKey: 'Key copied',
  confirmRevoke: 'Revoke this key?',
  keyOnceBanner: 'This key is shown only once — copy it now.',

  dashboardTitle: 'Admin dashboard',
  dashboardSubtitle: 'Destine overview',

  hideSection: 'Hide section',
  showApisUsed: 'Show APIs & services used',
  iaArchitectureSection: 'Destine IA',
  tabApia: 'AP-IA',
  teamSectionTitle: 'Destine team',
  inactiveUsersSection: 'Inactive users',
  inactiveUsersTitle: 'Inactive users (60+ days)',
  securityTitle: 'Security',
  securitySubtitle: 'Alerts and security score',
  securityScore: 'Security score',
  securityAlerts: 'Active alerts',
  securityNoAlerts: 'No active alerts.',
  securityAdvice: 'Security advice',
  regionalTitle: 'Regional languages (Guiana)',
  africaTitle: 'GPT Africa — Regional languages (Africa)',
  regionalSubtitle: 'Community translation corpus',
  regionalCount: 'contributions',
  regionalPerLang: 'By language',
  regionalAdd: 'Add a translation',
  regionalAddTitle: 'New regional translation',
  regionalCorrections: 'Proposed corrections',
  regionalCorrectionsShort: 'Corrections',
  regionalExport: 'Export (CSV)',
  regionalFormSrcPh: 'Source phrase (fr / en / nl)',
  regionalFormTgtPh: 'Regional translation',
  regionalFormOk: 'Confirmed',
  regionalFormMissing: 'Missing',
  regionalFormCorrection: 'Correction',
  regionalFormSave: 'Save',
  regionalFormSaving: 'Saving…',

  pinTitle: 'Protected access',
  pinShow: 'Show',
  pinHide: 'Hide',
  pinEdit: 'Edit PIN',
  pinOthers: 'Standard admin PIN',
  pinSuper: 'Super admin PIN',

  leaderboardTitle: 'Leaderboards',
  lbFollowers: 'Followers',
  lbLikes: 'Likes',
  lbPosts: 'Posts',
  lbComments: 'Comments',
  lbShares: 'Shares',
  lbReferrers: 'Referrers',
  lbLoading: 'Loading…',
  lbEmpty: 'No data',

  tileUpdateNotifTitle: 'Notify an update',
  tileUpdateNotifDesc: 'Send a push to every user',
  tileWeeklyCinemaTitle: 'Weekly cinema',
  tileWeeklyCinemaDesc: 'Publish the weekly cinema selection',
  tileUsersTitle: 'Users',
  tileUsersDesc: 'Manage accounts',
  tileBadgeTitle: 'Verified badges',
  tileBadgeDesc: 'Grant / revoke the blue badge',
  tileBadgeCandidatesTitle: 'Badge candidates',
  tileBadgeCandidatesDesc: 'Review verification requests',
  tileMyIaInsightsTitle: 'Déesse insights',
  tileMyIaInsightsDesc: 'Assistant usage stats',
  tileApiIntegrationsTitle: 'API integrations',
  tileApiIntegrationsDesc: 'External services status',
  tileModerationTitle: 'Moderation',
  tileModerationDesc: 'Reports and AI alerts',
  tileCinemaTitle: 'Cinema',
  tileCinemaDesc: 'Movies, news, releases',
  tileFranchisesTitle: 'Franchises',
  tileFranchisesDesc: 'Studios & sagas',
  tileTmdbTitle: 'TMDB sync',
  tileTmdbDesc: 'Sync releases & box office',
  tileFreeFilmTitle: 'Free films',
  tileFreeFilmDesc: 'Watchable catalog',
  tileVisibilityTitle: 'Sections visibility',
  tileVisibilityDesc: 'Hide / show Home, MyMouv, MyJob, free films',
  filmsLibresTitle: 'Public-domain films — visibility',
  filmsLibresDesc: 'This section is only shown to French-speaking users. You can hide it from everyone in one click.',
  filmsLibresVisible: 'Visible',
  filmsLibresHidden: 'Hidden',
  filmsLibresOk: 'Section visible',
  filmsLibresKo: 'Section hidden',
  filmsLibresError: 'Error',
  filmsPlaybackTitle: 'Public-domain films — playback mode',
  filmsPlaybackDesc: 'Choose how users watch YouTube films: embedded player directly in the app, or the web browser.',
  filmsPlaybackInApp: 'In-app',
  filmsPlaybackBrowser: 'Web browser',
  filmsPlaybackInAppOk: 'In-app playback enabled',
  filmsPlaybackBrowserOk: 'Browser playback enabled',
  giaAdminTitle: 'Green IA — protections & certifications',
  giaAdminProtected: 'Protected individuals',
  giaAdminApiaReqs: 'Green AP-IA requests',
  giaAdminReqSubtitle: 'Green AP-IA certificate requests',
  giaAdminApprove: 'Approve',
  giaAdminReject: 'Reject',
  giaAdminCert: 'Certificate',
  giaAdminIssued: 'Certificate issued',
  giaAdminRefused: 'Rejected',
  giaAdminError: 'Error',
  subAdminTitle: 'Paid subscriptions',
  subAdminEnabled: 'Enabled',
  subAdminDisabled: 'Hidden',
  subAdminOnMsg: 'Subscriptions enabled',
  subAdminOffMsg: 'Subscriptions hidden',
  subAdminError: 'Error',
  subAdminTotal: 'Total',
  subAdminActive: 'Active',
  subAdminPending: 'Pending',
  subAdminCancelled: 'Cancelled',
  subAdminRevenue: 'Estimated revenue',
  subAdminEmpty: 'No subscriptions.',
  subAdminCancelConfirm: 'Cancel this subscription?',
  photosTitle: 'Shared photos',
  videosTitle: 'Shared videos',
  greenIaSectionTitle: 'Green IA — Anti-deepfake registry',
  giaCertView: 'View certificate (preview)',
  giaLabelI18nNote: 'The label is automatically translated into the 13 languages — it is shown in the pro\'s language at the time of the request.',
  ratingsSectionTitle: 'User ratings — Releases & Box office',
  socialAgentsSectionTitle: 'Social Media AI Agents',
  avsTitle: 'Admin video studio',
  avsSub: 'Videos under 5 min — custom subtitles or dubbing. Free for admins.',
  avsTabSubs: 'Subtitles', avsTabDub: 'Dubbing',
  avsPick: 'Choose a video (5 min max)',
  avsLangSubs: 'Subtitle language', avsLangDub: 'Dubbing language', avsLangOriginal: 'Original language (no translation)',
  avsGenerate: 'Generate subtitles', avsGenerating: 'Transcribing…',
  avsEditHint: 'Freely edit the subtitle text below before burning it in.',
  avsBurn: 'Burn in & download', avsBurning: 'Burning…',
  avsDub: 'Dub the video', avsDubbing: 'Dubbing…',
  avsJobs: 'Dubs', avsNoJobs: 'No dubbing yet', avsDownload: 'Download',
  avsProcessing: 'Processing…', avsDone: 'Done', avsFailed: 'Failed',
  avsTooLong: 'Video too long (max 5 min)', avsTooBig: 'Video too large (max 150 MB)',
  avsOk: 'Started!', avsKo: 'Operation failed',
  avsRedownload: 'Download the last result again',
  teamSectionsBtn: 'Assigned sections', teamSectionsShort: 'sections',
  teamSectionsLabel: 'Quick-action sections this admin can access:',
  teamSectionsSave: 'Save', teamSectionsSaved: 'Sections updated',
  scpTitle: 'Auto-publish TikTok & YouTube Shorts',
  scpConnect: 'Connect', scpConnected: 'Connected',
  scpRedirectHint: 'Register these redirect URIs in the developer consoles:',
  scpPick: 'Choose a video', scpTitlePh: 'Title / caption', scpDescPh: 'Description (hashtags included)',
  scpPublish: 'Publish now', scpPublishing: 'Publishing…',
  scpPublished: 'Published!', scpPartial: 'Partial publish — check results', scpKo: 'Failed',
  scpJobs: 'Recent publications',
  scpAnalyze: 'AI Agent — Analyze my stats', scpAnalyzing: 'Analyzing…',
  scpFollowers: 'Followers', scpGain7: '7-day gain', scpGain30: '30-day gain',
  scpAiTitle: 'AI agent analysis',
  scvTitle: 'AI video creator agent',
  scvSub: 'The AI writes the script, generates visuals, voice-over and subtitles, then adds the Destine outro (logo + iOS/Android download). Preview then publish.',
  scvGenerate: 'Create a video', scvGenerating: 'Generating…', scvStarted: 'Generation started! (~2 min)',
  scvTopicWeekly: 'Movies of the week', scvTopicBox: 'Box office', scvTopicNews: 'Cinema news', scvTopicApp: 'Destine IA features',
  scvPickPlatform: 'Check at least one platform (TikTok/YouTube) below',
  scvSchedTitle: 'Daily auto-publish (up to 2 videos / day)',
  scvSchedAutoPub: 'Auto-publish to selected platforms',
  scvSchedSave: 'Save schedule', scvSchedSaved: 'Schedule saved',
  scvSchedHint: 'Local times (French Guiana, UTC-3). Topics alternate: cinema / box-office. Videos include the Destine logo and original music "Rêves de Cinéma".',
  scvSubClassic: 'Subtitles — Classic (white)', scvSubBold: 'Subtitles — Bold + shadow', scvSubYellow: 'Subtitles — YouTube yellow', scvSubCinema: 'Subtitles — Cinema (black box)', scvSubNeon: 'Subtitles — Pink neon',
  scvMusicPreview: 'Play music',
  legalDocsTileTitle: 'Legal documents', legalDocsTileDesc: 'T&C, sales terms, privacy, PDF & editor',
  edepotsTileTitle: 'e-Deposits', edepotsTileDesc: 'Deposit certificates, downloads, free test deposit (no payment)',
  edepotsAdminTitle: 'e-Deposits — Management & test', edepotsCreateFree: 'Create an admin deposit (free — no payment)',
  edepotsTitle: 'Title of the work', edepotsDescription: 'Description (optional)', edepotsCreateBtn: 'Create the free admin deposit',
  edepotsAll: 'All deposits', edepotsCert: 'PDF certificate', edepotsFile: 'Original file',
  adsPricingTitle: 'Ad prices & durations', adsPricingSub: 'Edit prices and broadcast durations. Changes are instantly visible to PRO accounts in Destine Ads.',
  adsPriceLabel: 'Price (€)', adsDurationLabel: 'Duration (days)', adsVideo: 'Video ad', adsAudio: 'Audio ad (spot)', adsImage: 'Poster ad (image)',
  adsPricingSave: 'Save', adsPricingSaved: 'Prices updated — instantly visible to PRO accounts',
  mymouvEventsTile: 'MyMouv — Events & ticketing',
  adminPitchTitle: 'Pitch Studio (free for admins)', adminPitchHint: 'Teleprompter + AI writing + 13-language translation + video/audio recording',
  legalDocsTitle: 'Legal documents', legalDocsSub: 'Manage PDFs and legal contents of the app. T&C and Sales terms are directly editable here.',
  legalPdfPresent: 'PDF present', legalNoPdf: 'No PDF', legalTextPresent: 'text OK', legalNoText: 'no text', legalEditable: 'Editable',
  legalReplace: 'Replace PDF', legalUpload: 'Upload PDF', legalDownload: 'Download', legalDeletePdf: 'Delete PDF', legalEdit: 'Edit content',
  legalSave: 'Save', legalExportPdf: 'Save & Download PDF',
  legalMustBePdf: 'File must be a PDF', legalUploaded: 'PDF saved', legalUploadKo: 'Upload error',
  legalConfirmDelete: 'Delete current PDF?', legalDeleted: 'PDF deleted', legalDeleteKo: 'Delete error',
  legalSaved: 'Content saved', legalSaveKo: 'Save error', legalPdfReady: 'PDF ready for download', legalExportKo: 'PDF export error',
  legalMdPlaceholder: 'Markdown content (## Heading, - list, **bold**)…', legalGenericError: 'Error', legalDownloadKo: 'Download failed',
  tileEmailTitle: 'Send an email',
  tileEmailDesc: 'Write to a user at their account address',
  emailModalTitle: 'Send an email', emailPickUser: 'Search for a user…', emailSubjectPh: 'Email title', emailBodyPh: 'Message text…', emailSendBtn: 'Send', emailSentOk: 'Email sent to', emailMissingFields: 'Recipient, title and text required', emailHistory: 'Recent sends',
  bizTasksSectionTitle: 'Administrative tasks & payments', bizDueNow: 'Due this month', bizMarkDone: 'Mark done', bizConfirmDelete: 'Delete this task?', bizMissing: 'Title and months required', bizAdd: 'Add', bizAddTask: 'Add a task', bizTitlePh: 'Task title', bizDescPh: 'Description (optional)',
  cyberTitle: 'AI Cyberdefense Agent', cyberProtected: 'Green IA protected people', cyberAudit: 'Chained audit entries', cyberFailed: 'Failed logins (7d)', cyberGreenTitle: 'How Green IA data is protected', cyberAnalyze: 'Run AI analysis', cyberVerify: 'Verify the registry', cyberChainOk: 'Audit chain intact', cyberEntries: 'sealed entries', cyberReportTitle: 'AI agent report',
  giaAssetsTitle: 'Green IA label & certificate',
  giaLabelDl: 'Download the label (SVG)',
  giaCertDl: 'Download the certificate (PDF preview)',
  subscriptionsSectionTitle: 'Premium subscriptions',
  photosFlag: 'Flag',
  photosUnflag: 'Restore',
  photosFlagged: 'flagged',
  photosFlaggedBadge: 'Flagged',
  photosEmpty: 'No shared photos yet',
  photosConfirmDelete: 'Delete this photo post?',
  photosDeleted: 'Photo post deleted',
  vtStatsTotal: 'Total videos',
  vtStatsFlagged: 'Flagged',
  vtStatsLikes: 'Total likes',
  vtStatsViews: 'Total views',
  saTitle: 'Social Media AI Agents',
  saTabCompose: 'Compose',
  saTabList: 'Scheduled',
  saTabMod: 'Moderation',
  saTitleField: 'Post title *',
  saBodyField: 'Post body *',
  saHashtags: 'Hashtags (e.g. #cinema #ai)',
  saMediaUrl: 'Media URL (image/video — optional)',
  saSchedule: 'Schedule',
  saMissing: 'Title, body and at least one platform are required',
  saScheduled: 'Post scheduled',
  saFlagged: 'Post scheduled but FLAGGED (forbidden terms detected)',
  saError: 'Error',
  saEmpty: 'No scheduled posts.',
  saDeleteConfirm: 'Delete this post?',
  saModTitle: 'Forbidden terms for auto-moderation',
  saModDesc: 'Any post containing one of these terms is automatically flagged and blocked.',
  saModAdd: 'Add a term…',
  saModSave: 'Save',
  saModSaved: 'Configuration saved',
  tileAdsValidationTitle: 'Ads validation',
  tileAdsValidationDesc: 'Approve pending ads',
  tileMonetizationTitle: 'Monetization',
  tileMonetizationDesc: 'Sponsors & creator offers',
  tileAdsTitle: 'Ads',
  tileAdsDesc: 'Active campaigns',

  tmdbSyncing: 'Syncing…',
  tmdbSyncDone: 'TMDB sync complete',
  tmdbSyncError: 'TMDB sync error',

  weeklyCinemaFilms: 'Selected films',
  weeklyCinemaAudience: 'Target audience',
  weeklyCinemaHint: 'Send a weekly notification with the cinema selection.',
  weeklyCinemaPreview: 'Preview',
  weeklyCinemaSend: 'Send',
  weeklyCinemaSending: 'Sending…',
  weeklyCinemaConfirm: 'Confirm sending to every user?',

  apiaKeysTitle: 'AP-IA keys',
  apiaTabPending: 'Pending',
  apiaTabApproved: 'Approved',
  apiaTabRefused: 'Refused',
  apiaRequestsEmpty: 'No requests',
  apiaSubmittedOn: 'Submitted on',
  apiaPurpose: 'Purpose',
  apiaReason: 'Reason',
  apiaApprove: 'Approve',
  apiaRefuse: 'Refuse',
  apiaApproved: 'Approved',
  apiaRefused: 'Refused',
  apiaRefusePrompt: 'Refusal reason (optional)',
  apiaKeyRevoke: 'Revoke',
  apiaKeyRevoked: 'Key revoked',
  apiaManualTitle: 'Generate a manual key',
  apiaManualHint: 'Create an AP-IA key without the user request flow (internal use).',
  apiaManualNamePh: 'Key name',
  apiaManualDescPh: 'Description (optional)',
  apiaManualGenerate: 'Generate key',

  teamTabTeam: 'Team',
  teamTabPartners: 'Partners',
  teamEmpty: 'No members',
  teamPartnersEmpty: 'No partners',
  teamGrant: 'Add a member',
  teamGrantConfirm: 'Add this user to the team?',
  teamGranted: 'Member added to the team',
  teamConfirmRevoke: 'Remove this member from the team?',
  teamRevoked: 'Member removed',
  teamJobType: 'Position',
  teamContract: 'Contract type',
  teamHours: 'Hours / week',
  teamSalaryEmail: 'Salary contact email',
  teamEntryDate: 'Start date',
  teamCvUpload: 'Upload CV (PDF)',
  teamCvReplace: 'Replace CV',
  teamCvView: 'View CV',
  teamCvUploaded: 'CV uploaded',
  teamPhoto: 'Photo',
  teamPhotoUpload: 'Upload photo',
  teamPhotoUploaded: 'Photo uploaded',
  teamProfileSave: 'Save profile',
  teamProfileSaved: 'Profile saved',
  teamSlip: 'Pay slip',
  teamSlipPdf: 'Pay slip (PDF)',
  teamSlipSend: 'Send slip',
  teamSlipSent: 'Slip sent',
  teamSlipStored: 'Slip archived',
  teamSlipFileRequired: 'PDF file required',
  teamSlipPeriodInvalid: 'Invalid period (format YYYY-MM)',
  teamPartnerAdd: 'Add a partner',
  teamPartnerAdded: 'Partner added',
  teamPartnerNew: 'New partner',
  teamPartnerType: 'Type',
  teamPartnerShareholder: 'Shareholder',
  teamPartnerConfirmDelete: 'Delete this partner?',

  slipsTab: 'Pay slips',
  slipsEmpty: 'No slips',
  slipsDownload: 'Download',
  slipsDelete: 'Delete',
  slipsConfirmDelete: 'Delete this slip?',

  usersByLangTitle: 'Users by language',
  usersByLangSubtitle: 'Breakdown across the 13 supported languages',
  usersByLangOther: 'Other / not set',
  usersByLangTotal: 'Total',
  usersByLangEmpty: 'No data',
  usersByLangClickHint: 'Click to see the breakdown',
};

// --- Spanish --------------------------------------------------------------
const es = {
  exit: 'Volver',
  cancel: 'Cancelar',
  copy: 'Copiar',
  copiedKey: 'Clave copiada',
  confirmRevoke: '¿Revocar esta clave?',
  keyOnceBanner: 'Esta clave se muestra una sola vez — cópiala ahora.',

  dashboardTitle: 'Panel de administración',
  dashboardSubtitle: 'Vista general de Destine',

  hideSection: 'Ocultar sección',
  showApisUsed: 'Ver API y servicios usados',
  iaArchitectureSection: 'Destine IA',
  tabApia: 'AP-IA',
  teamSectionTitle: 'Equipo Destine',
  inactiveUsersSection: 'Usuarios inactivos',
  inactiveUsersTitle: 'Usuarios inactivos (60+ días)',
  securityTitle: 'Seguridad',
  securitySubtitle: 'Alertas y puntuación de seguridad',
  securityScore: 'Puntuación de seguridad',
  securityAlerts: 'Alertas activas',
  securityNoAlerts: 'Sin alertas activas.',
  securityAdvice: 'Consejos de seguridad',
  regionalTitle: 'Lenguas regionales (Guayana)',
  africaTitle: 'GPT Africa — Lenguas regionales (África)',
  regionalSubtitle: 'Corpus de traducciones comunitarias',
  regionalCount: 'contribuciones',
  regionalPerLang: 'Por idioma',
  regionalAdd: 'Añadir una traducción',
  regionalAddTitle: 'Nueva traducción regional',
  regionalCorrections: 'Correcciones propuestas',
  regionalCorrectionsShort: 'Correcciones',
  regionalExport: 'Exportar (CSV)',
  regionalFormSrcPh: 'Frase de origen (fr / en / nl)',
  regionalFormTgtPh: 'Traducción regional',
  regionalFormOk: 'Confirmada',
  regionalFormMissing: 'Falta',
  regionalFormCorrection: 'Corrección',
  regionalFormSave: 'Guardar',
  regionalFormSaving: 'Guardando…',

  pinTitle: 'Acceso protegido',
  pinShow: 'Mostrar',
  pinHide: 'Ocultar',
  pinEdit: 'Cambiar el PIN',
  pinOthers: 'PIN admin estándar',
  pinSuper: 'PIN super admin',

  leaderboardTitle: 'Clasificaciones',
  lbFollowers: 'Seguidores',
  lbLikes: 'Me gusta',
  lbPosts: 'Publicaciones',
  lbComments: 'Comentarios',
  lbShares: 'Compartidos',
  lbReferrers: 'Referencias',
  lbLoading: 'Cargando…',
  lbEmpty: 'Sin datos',

  tileUpdateNotifTitle: 'Notificar una actualización',
  tileUpdateNotifDesc: 'Enviar una notificación a todos los usuarios',
  tileWeeklyCinemaTitle: 'Cine de la semana',
  tileWeeklyCinemaDesc: 'Publicar la selección semanal de cine',
  tileUsersTitle: 'Usuarios',
  tileUsersDesc: 'Gestionar las cuentas',
  tileBadgeTitle: 'Insignias verificadas',
  tileBadgeDesc: 'Otorgar/retirar la insignia azul',
  tileBadgeCandidatesTitle: 'Solicitudes de insignia',
  tileBadgeCandidatesDesc: 'Revisar las solicitudes de verificación',
  tileMyIaInsightsTitle: 'Insights Déesse',
  tileMyIaInsightsDesc: 'Estadísticas de uso del asistente',
  tileApiIntegrationsTitle: 'Integraciones API',
  tileApiIntegrationsDesc: 'Estado de los servicios externos',
  tileModerationTitle: 'Moderación',
  tileModerationDesc: 'Reportes y alertas de IA',
  tileCinemaTitle: 'Cine',
  tileCinemaDesc: 'Películas, noticias, estrenos',
  tileFranchisesTitle: 'Franquicias',
  tileFranchisesDesc: 'Estudios y sagas',
  tileTmdbTitle: 'Sincronizar TMDB',
  tileTmdbDesc: 'Sincronizar estrenos y taquilla',
  tileFreeFilmTitle: 'Películas gratis',
  tileFreeFilmDesc: 'Catálogo disponible',
  tileVisibilityTitle: 'Visibilidad de secciones',
  tileVisibilityDesc: 'Ocultar / mostrar Inicio, MyMouv, MyJob, películas libres',
  filmsLibresTitle: 'Películas de dominio público — visibilidad',
  filmsLibresDesc: 'Esta sección solo es visible para usuarios francófonos. Puede ocultarla a todos con un clic.',
  filmsLibresVisible: 'Visible',
  filmsLibresHidden: 'Oculta',
  filmsLibresOk: 'Sección visible',
  filmsLibresKo: 'Sección oculta',
  filmsLibresError: 'Error',
  filmsPlaybackTitle: 'Películas de dominio público — modo de reproducción',
  filmsPlaybackDesc: 'Elija cómo los usuarios ven las películas de YouTube: reproductor integrado en la app o navegador web.',
  filmsPlaybackInApp: 'En la app',
  filmsPlaybackBrowser: 'Navegador web',
  filmsPlaybackInAppOk: 'Reproducción en la app activada',
  filmsPlaybackBrowserOk: 'Reproducción en navegador activada',
  giaAdminTitle: 'Green IA — protecciones y certificaciones',
  giaAdminProtected: 'Personas protegidas',
  giaAdminApiaReqs: 'Solicitudes Green AP-IA',
  giaAdminReqSubtitle: 'Solicitudes de certificado Green AP-IA',
  giaAdminApprove: 'Aprobar',
  giaAdminReject: 'Rechazar',
  giaAdminCert: 'Certificado',
  giaAdminIssued: 'Certificado emitido',
  giaAdminRefused: 'Rechazado',
  giaAdminError: 'Error',
  subAdminTitle: 'Suscripciones pagas',
  subAdminEnabled: 'Activas',
  subAdminDisabled: 'Ocultas',
  subAdminOnMsg: 'Suscripciones activadas',
  subAdminOffMsg: 'Suscripciones ocultadas',
  subAdminError: 'Error',
  subAdminTotal: 'Total',
  subAdminActive: 'Activas',
  subAdminPending: 'Pendientes',
  subAdminCancelled: 'Canceladas',
  subAdminRevenue: 'Ingresos estimados',
  subAdminEmpty: 'Sin suscripciones.',
  subAdminCancelConfirm: '¿Cancelar esta suscripción?',
  photosTitle: 'Fotos compartidas',
  videosTitle: 'Vídeos compartidos',
  greenIaSectionTitle: 'Green IA — Registro anti-deepfake',
  giaCertView: 'Ver el certificado (vista previa)',
  giaLabelI18nNote: 'La etiqueta se traduce automáticamente a los 13 idiomas y se muestra en el idioma del profesional en el momento de su solicitud.',
  ratingsSectionTitle: 'Notas de usuarios — Estrenos y taquilla',
  socialAgentsSectionTitle: 'Agentes IA de redes sociales',
  avsTitle: 'Estudio de vídeo admin',
  avsSub: 'Vídeos de menos de 5 min — subtítulos personalizados o doblaje. Gratis para los admins.',
  avsTabSubs: 'Subtítulos', avsTabDub: 'Doblaje',
  avsPick: 'Elegir un vídeo (máx. 5 min)',
  avsLangSubs: 'Idioma de los subtítulos', avsLangDub: 'Idioma del doblaje', avsLangOriginal: 'Idioma original (sin traducción)',
  avsGenerate: 'Generar subtítulos', avsGenerating: 'Transcribiendo…',
  avsEditHint: 'Edita libremente el texto de los subtítulos antes de incrustarlos.',
  avsBurn: 'Incrustar y descargar', avsBurning: 'Incrustando…',
  avsDub: 'Doblar el vídeo', avsDubbing: 'Doblando…',
  avsJobs: 'Doblajes', avsNoJobs: 'Ningún doblaje por ahora', avsDownload: 'Descargar',
  avsProcessing: 'En curso…', avsDone: 'Terminado', avsFailed: 'Fallido',
  avsTooLong: 'Vídeo demasiado largo (máx. 5 min)', avsTooBig: 'Vídeo demasiado pesado (máx. 150 MB)',
  avsOk: '¡En marcha!', avsKo: 'Operación fallida',
  avsRedownload: 'Volver a descargar el último resultado',
  teamSectionsBtn: 'Secciones asignadas', teamSectionsShort: 'secciones',
  teamSectionsLabel: 'Secciones (Acciones rápidas) accesibles para este admin:',
  teamSectionsSave: 'Guardar', teamSectionsSaved: 'Secciones actualizadas',
  scpTitle: 'Publicación auto TikTok y YouTube Shorts',
  scpConnect: 'Conectar', scpConnected: 'Conectado',
  scpRedirectHint: 'Registra estas URI de redirección en las consolas de desarrollador:',
  scpPick: 'Elegir un vídeo', scpTitlePh: 'Título / leyenda', scpDescPh: 'Descripción (con hashtags)',
  scpPublish: 'Publicar ahora', scpPublishing: 'Publicando…',
  scpPublished: '¡Publicado!', scpPartial: 'Publicación parcial — revisa los resultados', scpKo: 'Fallo',
  scpJobs: 'Publicaciones recientes',
  scpAnalyze: 'Agente IA — Analizar mis estadísticas', scpAnalyzing: 'Analizando…',
  scpFollowers: 'Seguidores', scpGain7: 'Ganancia 7 d', scpGain30: 'Ganancia 30 d',
  scpAiTitle: 'Análisis del agente IA',
  scvTitle: 'Agente IA creador de vídeos',
  scvSub: 'La IA escribe el guion, genera los visuales, la voz en off y los subtítulos, y añade el cierre Destine (logo + descarga iOS/Android). Previsualiza y publica.',
  scvGenerate: 'Crear un vídeo', scvGenerating: 'Generando…', scvStarted: '¡Generación iniciada! (~2 min)',
  scvTopicWeekly: 'Películas de la semana', scvTopicBox: 'Taquilla', scvTopicNews: 'Noticias de cine', scvTopicApp: 'Funciones de Destine IA',
  scvPickPlatform: 'Marca al menos una plataforma (TikTok/YouTube) abajo',
  scvSchedTitle: 'Publicación auto diaria (hasta 2 vídeos / día)',
  scvSchedAutoPub: 'Publicar automáticamente en las plataformas marcadas',
  scvSchedSave: 'Guardar programación', scvSchedSaved: 'Programación guardada',
  scvSchedHint: 'Horas locales (Guayana Francesa, UTC-3). Temas alternos: cine / taquilla. Los vídeos incluyen el logo Destine y la música original «Rêves de Cinéma».',
  scvSubClassic: 'Subtítulos — Clásico (blanco)', scvSubBold: 'Subtítulos — Negrita + sombra', scvSubYellow: 'Subtítulos — Amarillo YouTube', scvSubCinema: 'Subtítulos — Cine (caja negra)', scvSubNeon: 'Subtítulos — Neón rosa',
  scvMusicPreview: 'Escuchar la música',
  legalDocsTileTitle: 'Documentos legales', legalDocsTileDesc: 'Términos, ventas, privacidad, PDF y editor',
  edepotsTileTitle: 'e-Depósitos', edepotsTileDesc: 'Certificados de depósito, descargas, depósito de prueba gratuito (sin pago)',
  edepotsAdminTitle: 'e-Depósitos — Gestión y prueba', edepotsCreateFree: 'Crear un depósito admin (gratis — sin pago)',
  edepotsTitle: 'Título de la obra', edepotsDescription: 'Descripción (opcional)', edepotsCreateBtn: 'Crear el depósito admin gratuito',
  edepotsAll: 'Todos los depósitos', edepotsCert: 'Certificado PDF', edepotsFile: 'Archivo original',
  adsPricingTitle: 'Precios y duraciones de anuncios', adsPricingSub: 'Modifica precios y duraciones de difusión. Los cambios son visibles al instante para las cuentas PRO en Destine Ads.',
  adsPriceLabel: 'Precio (€)', adsDurationLabel: 'Duración (días)', adsVideo: 'Anuncio de vídeo', adsAudio: 'Anuncio de audio (spot)', adsImage: 'Anuncio cartel (imagen)',
  adsPricingSave: 'Guardar', adsPricingSaved: 'Precios actualizados — visibles al instante para PRO',
  mymouvEventsTile: 'MyMouv — Eventos y entradas',
  adminPitchTitle: 'Estudio Pitch (gratis para admins)', adminPitchHint: 'Teleprompter + redacción IA + traducción a 13 idiomas + grabación vídeo/audio',
  legalDocsTitle: 'Documentos legales', legalDocsSub: 'Gestiona los PDF y contenidos legales. Los CGU y CGV son editables aquí.',
  legalPdfPresent: 'PDF presente', legalNoPdf: 'Sin PDF', legalTextPresent: 'texto OK', legalNoText: 'sin texto', legalEditable: 'Editable',
  legalReplace: 'Reemplazar PDF', legalUpload: 'Subir PDF', legalDownload: 'Descargar', legalDeletePdf: 'Eliminar PDF', legalEdit: 'Editar contenido',
  legalSave: 'Guardar', legalExportPdf: 'Guardar y descargar el PDF',
  legalMustBePdf: 'El archivo debe ser un PDF', legalUploaded: 'PDF guardado', legalUploadKo: 'Error de subida',
  legalConfirmDelete: '¿Eliminar el PDF actual?', legalDeleted: 'PDF eliminado', legalDeleteKo: 'Error al eliminar',
  legalSaved: 'Contenido guardado', legalSaveKo: 'Error al guardar', legalPdfReady: 'PDF listo para descargar', legalExportKo: 'Error de exportación PDF',
  legalMdPlaceholder: 'Contenido Markdown (## Título, - lista, **negrita**)…', legalGenericError: 'Error', legalDownloadKo: 'No se pudo descargar',
  tileEmailTitle: 'Enviar un email',
  tileEmailDesc: 'Escribir a un usuario a su dirección de cuenta',
  emailModalTitle: 'Enviar un email', emailPickUser: 'Buscar un usuario…', emailSubjectPh: 'Título del email', emailBodyPh: 'Texto del mensaje…', emailSendBtn: 'Enviar', emailSentOk: 'Email enviado a', emailMissingFields: 'Destinatario, título y texto requeridos', emailHistory: 'Últimos envíos',
  bizTasksSectionTitle: 'Trámites administrativos y pagos', bizDueNow: 'Para este mes', bizMarkDone: 'Marcar hecho', bizConfirmDelete: '¿Eliminar este trámite?', bizMissing: 'Título y meses requeridos', bizAdd: 'Añadir', bizAddTask: 'Añadir un trámite', bizTitlePh: 'Título del trámite', bizDescPh: 'Descripción (opcional)',
  cyberTitle: 'Agente IA de Ciberdefensa', cyberProtected: 'Personas protegidas Green IA', cyberAudit: 'Entradas de auditoría encadenadas', cyberFailed: 'Inicios fallidos (7d)', cyberGreenTitle: 'Cómo se protegen los datos Green IA', cyberAnalyze: 'Lanzar análisis IA', cyberVerify: 'Verificar el registro', cyberChainOk: 'Cadena de auditoría intacta', cyberEntries: 'entradas selladas', cyberReportTitle: 'Informe del agente IA',
  giaAssetsTitle: 'Etiqueta y certificado Green IA',
  giaLabelDl: 'Descargar la etiqueta (SVG)',
  giaCertDl: 'Descargar el certificado (vista previa PDF)',
  subscriptionsSectionTitle: 'Suscripciones Premium',
  photosFlag: 'Señalar',
  photosUnflag: 'Restaurar',
  photosFlagged: 'señaladas',
  photosFlaggedBadge: 'Señalada',
  photosEmpty: 'Aún no hay fotos compartidas',
  photosConfirmDelete: '¿Eliminar esta publicación de fotos?',
  photosDeleted: 'Publicación eliminada',
  vtStatsTotal: 'Vídeos totales',
  vtStatsFlagged: 'Denunciados',
  vtStatsLikes: 'Likes acumulados',
  vtStatsViews: 'Vistas acumuladas',
  saTitle: 'Agentes IA Redes sociales',
  saTabCompose: 'Redactar',
  saTabList: 'Programados',
  saTabMod: 'Moderación',
  saTitleField: 'Título del post *',
  saBodyField: 'Contenido *',
  saHashtags: 'Hashtags (ej: #cine #ia)',
  saMediaUrl: 'URL del medio (imagen/vídeo — opcional)',
  saSchedule: 'Programar',
  saMissing: 'Título, contenido y al menos una plataforma requeridos',
  saScheduled: 'Post programado',
  saFlagged: 'Post programado pero SEÑALADO (términos prohibidos)',
  saError: 'Error',
  saEmpty: 'Sin posts programados.',
  saDeleteConfirm: '¿Eliminar este post?',
  saModTitle: 'Términos prohibidos para auto-moderación',
  saModDesc: 'Todo post que contenga uno de estos términos será señalado y bloqueado.',
  saModAdd: 'Añadir un término…',
  saModSave: 'Guardar',
  saModSaved: 'Configuración guardada',
  tileAdsValidationTitle: 'Validación de anuncios',
  tileAdsValidationDesc: 'Aprobar anuncios pendientes',
  tileMonetizationTitle: 'Monetización',
  tileMonetizationDesc: 'Patrocinadores y ofertas de creadores',
  tileAdsTitle: 'Anuncios',
  tileAdsDesc: 'Campañas activas',

  tmdbSyncing: 'Sincronizando…',
  tmdbSyncDone: 'Sincronización TMDB completada',
  tmdbSyncError: 'Error de sincronización TMDB',

  weeklyCinemaFilms: 'Películas seleccionadas',
  weeklyCinemaAudience: 'Público objetivo',
  weeklyCinemaHint: 'Envíe una notificación semanal con la selección de cine.',
  weeklyCinemaPreview: 'Vista previa',
  weeklyCinemaSend: 'Enviar',
  weeklyCinemaSending: 'Enviando…',
  weeklyCinemaConfirm: '¿Confirmar envío a todos los usuarios?',

  apiaKeysTitle: 'Claves AP-IA',
  apiaTabPending: 'Pendientes',
  apiaTabApproved: 'Aprobadas',
  apiaTabRefused: 'Rechazadas',
  apiaRequestsEmpty: 'Sin solicitudes',
  apiaSubmittedOn: 'Enviada el',
  apiaPurpose: 'Objeto',
  apiaReason: 'Motivo',
  apiaApprove: 'Aprobar',
  apiaRefuse: 'Rechazar',
  apiaApproved: 'Aprobada',
  apiaRefused: 'Rechazada',
  apiaRefusePrompt: 'Motivo del rechazo (opcional)',
  apiaKeyRevoke: 'Revocar',
  apiaKeyRevoked: 'Clave revocada',
  apiaManualTitle: 'Generar clave manual',
  apiaManualHint: 'Crear una clave AP-IA sin pasar por la solicitud (uso interno).',
  apiaManualNamePh: 'Nombre de la clave',
  apiaManualDescPh: 'Descripción (opcional)',
  apiaManualGenerate: 'Generar clave',

  teamTabTeam: 'Equipo',
  teamTabPartners: 'Socios',
  teamEmpty: 'Sin miembros',
  teamPartnersEmpty: 'Sin socios',
  teamGrant: 'Añadir un miembro',
  teamGrantConfirm: '¿Añadir este usuario al equipo?',
  teamGranted: 'Miembro añadido al equipo',
  teamConfirmRevoke: '¿Retirar este miembro del equipo?',
  teamRevoked: 'Miembro retirado',
  teamJobType: 'Puesto',
  teamContract: 'Tipo de contrato',
  teamHours: 'Horas / semana',
  teamSalaryEmail: 'Email de contacto salario',
  teamEntryDate: 'Fecha de entrada',
  teamCvUpload: 'Subir CV (PDF)',
  teamCvReplace: 'Reemplazar CV',
  teamCvView: 'Ver CV',
  teamCvUploaded: 'CV subido',
  teamPhoto: 'Foto',
  teamPhotoUpload: 'Subir foto',
  teamPhotoUploaded: 'Foto subida',
  teamProfileSave: 'Guardar perfil',
  teamProfileSaved: 'Perfil guardado',
  teamSlip: 'Nómina',
  teamSlipPdf: 'Nómina (PDF)',
  teamSlipSend: 'Enviar la nómina',
  teamSlipSent: 'Nómina enviada',
  teamSlipStored: 'Nómina archivada',
  teamSlipFileRequired: 'Se requiere un archivo PDF',
  teamSlipPeriodInvalid: 'Periodo inválido (formato YYYY-MM)',
  teamPartnerAdd: 'Añadir un socio',
  teamPartnerAdded: 'Socio añadido',
  teamPartnerNew: 'Nuevo socio',
  teamPartnerType: 'Tipo',
  teamPartnerShareholder: 'Accionista',
  teamPartnerConfirmDelete: '¿Eliminar este socio?',

  slipsTab: 'Nóminas',
  slipsEmpty: 'Sin nóminas',
  slipsDownload: 'Descargar',
  slipsDelete: 'Eliminar',
  slipsConfirmDelete: '¿Eliminar esta nómina?',

  usersByLangTitle: 'Usuarios por idioma',
  usersByLangSubtitle: 'Distribución en los 13 idiomas admitidos',
  usersByLangOther: 'Otro / sin definir',
  usersByLangTotal: 'Total',
  usersByLangEmpty: 'Sin datos',
  usersByLangClickHint: 'Haz clic para ver la distribución',
};

// --- Chinese (Simplified) -------------------------------------------------
const zh = {
  exit: '返回',
  cancel: '取消',
  copy: '复制',
  copiedKey: '密钥已复制',
  confirmRevoke: '撤销此密钥？',
  keyOnceBanner: '此密钥仅显示一次，请立即复制。',

  dashboardTitle: '管理仪表板',
  dashboardSubtitle: 'Destine 概览',

  hideSection: '隐藏此部分',
  showApisUsed: '查看使用的 API 和服务',
  iaArchitectureSection: 'Destine IA',
  tabApia: 'AP-IA',
  teamSectionTitle: 'Destine 团队',
  inactiveUsersSection: '不活跃用户',
  inactiveUsersTitle: '不活跃用户（60+ 天）',
  securityTitle: '安全',
  securitySubtitle: '警报与安全评分',
  securityScore: '安全评分',
  securityAlerts: '活动警报',
  securityNoAlerts: '暂无警报。',
  securityAdvice: '安全建议',
  regionalTitle: '地区语言（法属圭亚那）',
  africaTitle: 'GPT Africa — 地区语言（非洲）',
  regionalSubtitle: '社区翻译语料库',
  regionalCount: '条贡献',
  regionalPerLang: '按语言',
  regionalAdd: '添加翻译',
  regionalAddTitle: '新增地区翻译',
  regionalCorrections: '待处理更正',
  regionalCorrectionsShort: '更正',
  regionalExport: '导出（CSV）',
  regionalFormSrcPh: '源短语（fr / en / nl）',
  regionalFormTgtPh: '地区语言译文',
  regionalFormOk: '已确认',
  regionalFormMissing: '缺失',
  regionalFormCorrection: '更正',
  regionalFormSave: '保存',
  regionalFormSaving: '保存中…',

  pinTitle: '受保护的访问',
  pinShow: '显示',
  pinHide: '隐藏',
  pinEdit: '修改 PIN',
  pinOthers: '普通管理员 PIN',
  pinSuper: '超级管理员 PIN',

  leaderboardTitle: '排行榜',
  lbFollowers: '关注',
  lbLikes: '点赞',
  lbPosts: '帖子',
  lbComments: '评论',
  lbShares: '分享',
  lbReferrers: '推荐',
  lbLoading: '加载中…',
  lbEmpty: '暂无数据',

  tileUpdateNotifTitle: '通知更新',
  tileUpdateNotifDesc: '向所有用户发送推送',
  tileWeeklyCinemaTitle: '每周电影',
  tileWeeklyCinemaDesc: '发布本周电影精选',
  tileUsersTitle: '用户',
  tileUsersDesc: '管理账户',
  tileBadgeTitle: '认证徽章',
  tileBadgeDesc: '授予/撤销蓝色徽章',
  tileBadgeCandidatesTitle: '徽章申请',
  tileBadgeCandidatesDesc: '审核验证申请',
  tileMyIaInsightsTitle: 'Déesse 洞察',
  tileMyIaInsightsDesc: '助手使用统计',
  tileApiIntegrationsTitle: 'API 集成',
  tileApiIntegrationsDesc: '外部服务状态',
  tileModerationTitle: '审核',
  tileModerationDesc: '举报和 AI 警报',
  tileCinemaTitle: '电影',
  tileCinemaDesc: '电影、新闻、上映',
  tileFranchisesTitle: '系列',
  tileFranchisesDesc: '制片厂和 IP',
  tileTmdbTitle: 'TMDB 同步',
  tileTmdbDesc: '同步上映和票房',
  tileFreeFilmTitle: '免费电影',
  tileFreeFilmDesc: '可观看的目录',
  tileVisibilityTitle: '版块可见性',
  tileVisibilityDesc: '隐藏/显示首页、MyMouv、MyJob、免费电影',
  filmsLibresTitle: '公共版权电影 — 可见性',
  filmsLibresDesc: '此部分仅对法语用户可见。您可以一键对所有人隐藏。',
  filmsLibresVisible: '可见',
  filmsLibresHidden: '已隐藏',
  filmsLibresOk: '版块已显示',
  filmsLibresKo: '版块已隐藏',
  filmsLibresError: '错误',
  filmsPlaybackTitle: '公版电影 — 播放方式',
  filmsPlaybackDesc: '选择用户观看 YouTube 电影的方式：应用内嵌播放器，或网页浏览器。',
  filmsPlaybackInApp: '应用内',
  filmsPlaybackBrowser: '网页浏览器',
  filmsPlaybackInAppOk: '已启用应用内播放',
  filmsPlaybackBrowserOk: '已启用浏览器播放',
  giaAdminTitle: 'Green IA — 保护与认证',
  giaAdminProtected: '受保护个人',
  giaAdminApiaReqs: 'Green AP-IA 申请',
  giaAdminReqSubtitle: 'Green AP-IA 证书申请',
  giaAdminApprove: '通过',
  giaAdminReject: '拒绝',
  giaAdminCert: '证书',
  giaAdminIssued: '证书已核发',
  giaAdminRefused: '已拒绝',
  giaAdminError: '错误',
  subAdminTitle: '付费订阅',
  subAdminEnabled: '已启用',
  subAdminDisabled: '已隐藏',
  subAdminOnMsg: '订阅已启用',
  subAdminOffMsg: '订阅已隐藏',
  subAdminError: '错误',
  subAdminTotal: '总数',
  subAdminActive: '有效',
  subAdminPending: '待处理',
  subAdminCancelled: '已取消',
  subAdminRevenue: '预估营收',
  subAdminEmpty: '暂无订阅。',
  subAdminCancelConfirm: '取消此订阅？',
  photosTitle: '共享照片',
  videosTitle: '共享视频',
  greenIaSectionTitle: 'Green IA — 反深度伪造注册表',
  giaCertView: '查看证书（预览）',
  giaLabelI18nNote: '标签会自动翻译为 13 种语言，并以专业用户申请时的语言显示。',
  ratingsSectionTitle: '用户评分 — 上映与票房',
  socialAgentsSectionTitle: '社交媒体AI代理',
  avsTitle: '管理员视频工作室',
  avsSub: '5分钟以内的视频 — 自定义字幕或配音。管理员免费。',
  avsTabSubs: '字幕', avsTabDub: '配音',
  avsPick: '选择视频（最长5分钟）',
  avsLangSubs: '字幕语言', avsLangDub: '配音语言', avsLangOriginal: '原始语言（不翻译）',
  avsGenerate: '生成字幕', avsGenerating: '转录中…',
  avsEditHint: '在嵌入之前可自由编辑下方的字幕文本。',
  avsBurn: '嵌入并下载', avsBurning: '嵌入中…',
  avsDub: '为视频配音', avsDubbing: '配音中…',
  avsJobs: '配音记录', avsNoJobs: '暂无配音', avsDownload: '下载',
  avsProcessing: '处理中…', avsDone: '已完成', avsFailed: '失败',
  avsTooLong: '视频太长（最长5分钟）', avsTooBig: '视频太大（最大150 MB）',
  avsOk: '已开始！', avsKo: '操作失败',
  avsRedownload: '再次下载最新结果',
  teamSectionsBtn: '已分配的板块', teamSectionsShort: '个板块',
  teamSectionsLabel: '此管理员可访问的板块（快捷操作）：',
  teamSectionsSave: '保存', teamSectionsSaved: '板块已更新',
  scpTitle: 'TikTok和YouTube Shorts自动发布',
  scpConnect: '连接', scpConnected: '已连接',
  scpRedirectHint: '请在开发者控制台注册以下重定向URI：',
  scpPick: '选择视频', scpTitlePh: '标题/说明', scpDescPh: '描述（含标签）',
  scpPublish: '立即发布', scpPublishing: '发布中…',
  scpPublished: '已发布！', scpPartial: '部分发布 — 请检查结果', scpKo: '失败',
  scpJobs: '最近发布',
  scpAnalyze: 'AI代理 — 分析我的数据', scpAnalyzing: '分析中…',
  scpFollowers: '粉丝', scpGain7: '7天增长', scpGain30: '30天增长',
  scpAiTitle: 'AI代理分析',
  scvTitle: 'AI视频创作代理',
  scvSub: 'AI撰写脚本、生成画面、配音和字幕，并添加Destine片尾（标志 + iOS/Android下载）。预览后发布。',
  scvGenerate: '创建视频', scvGenerating: '生成中…', scvStarted: '已开始生成！（约2分钟）',
  scvTopicWeekly: '本周电影', scvTopicBox: '票房', scvTopicNews: '电影资讯', scvTopicApp: 'Destine IA功能',
  scvPickPlatform: '请在下方勾选至少一个平台（TikTok/YouTube）',
  scvSchedTitle: '每日自动发布（最多 2 个视频 / 天）',
  scvSchedAutoPub: '自动发布到所选平台',
  scvSchedSave: '保存排期', scvSchedSaved: '排期已保存',
  scvSchedHint: '本地时间（法属圭亚那，UTC-3）。主题交替：电影 / 票房。视频包含 Destine 标志和原创音乐《Rêves de Cinéma》。',
  scvSubClassic: '字幕 — 经典（白色）', scvSubBold: '字幕 — 粗体+阴影', scvSubYellow: '字幕 — YouTube 黄', scvSubCinema: '字幕 — 电影黑框', scvSubNeon: '字幕 — 霓虹粉',
  scvMusicPreview: '播放背景音乐',
  legalDocsTileTitle: '法律文件', legalDocsTileDesc: '使用条款、销售条款、隐私、PDF 与编辑器',
  edepotsTileTitle: '电子存证', edepotsTileDesc: '存证证书、下载、免费测试存证（无需付款）',
  edepotsAdminTitle: '电子存证 — 管理与测试', edepotsCreateFree: '创建管理员存证（免费 — 无需付款）',
  edepotsTitle: '作品标题', edepotsDescription: '描述（可选）', edepotsCreateBtn: '创建免费管理员存证',
  edepotsAll: '所有存证', edepotsCert: 'PDF 证书', edepotsFile: '原始文件',
  adsPricingTitle: '广告价格与时长', adsPricingSub: '修改价格和投放时长。更改会立即在 Destine Ads 中对 PRO 账户可见。',
  adsPriceLabel: '价格（€）', adsDurationLabel: '时长（天）', adsVideo: '视频广告', adsAudio: '音频广告', adsImage: '海报广告（图片）',
  adsPricingSave: '保存', adsPricingSaved: '价格已更新 — 立即对 PRO 可见',
  mymouvEventsTile: 'MyMouv — 活动与票务',
  adminPitchTitle: '演讲工作室（管理员免费）', adminPitchHint: '提词器 + AI 撰写 + 13 种语言翻译 + 视频/音频录制',
  legalDocsTitle: '法律文件', legalDocsSub: '管理 App 的 PDF 与法律文本。使用条款和销售条款可以直接编辑。',
  legalPdfPresent: '已上传 PDF', legalNoPdf: '无 PDF', legalTextPresent: '文本 OK', legalNoText: '无文本', legalEditable: '可编辑',
  legalReplace: '替换 PDF', legalUpload: '上传 PDF', legalDownload: '下载', legalDeletePdf: '删除 PDF', legalEdit: '编辑内容',
  legalSave: '保存', legalExportPdf: '保存并下载 PDF',
  legalMustBePdf: '文件必须为 PDF', legalUploaded: 'PDF 已保存', legalUploadKo: '上传失败',
  legalConfirmDelete: '确定删除当前 PDF？', legalDeleted: 'PDF 已删除', legalDeleteKo: '删除失败',
  legalSaved: '内容已保存', legalSaveKo: '保存失败', legalPdfReady: 'PDF 已准备好下载', legalExportKo: 'PDF 导出失败',
  legalMdPlaceholder: 'Markdown 内容 (## 标题, - 列表, **粗体**)…', legalGenericError: '错误', legalDownloadKo: '下载失败',
  tileEmailTitle: '发送邮件',
  tileEmailDesc: '向用户的账户邮箱发送邮件',
  emailModalTitle: '发送邮件', emailPickUser: '搜索用户…', emailSubjectPh: '邮件标题', emailBodyPh: '邮件内容…', emailSendBtn: '发送', emailSentOk: '邮件已发送至', emailMissingFields: '需要收件人、标题和内容', emailHistory: '最近发送',
  bizTasksSectionTitle: '行政事务与付款', bizDueNow: '本月待办', bizMarkDone: '标记完成', bizConfirmDelete: '删除此事务？', bizMissing: '需要标题和月份', bizAdd: '添加', bizAddTask: '添加事务', bizTitlePh: '事务标题', bizDescPh: '描述（可选）',
  cyberTitle: 'AI 网络防御代理', cyberProtected: 'Green IA 受保护人数', cyberAudit: '链式审计条目', cyberFailed: '登录失败（7天）', cyberGreenTitle: 'Green IA 数据如何受到保护', cyberAnalyze: '启动 AI 分析', cyberVerify: '验证注册表', cyberChainOk: '审计链完整', cyberEntries: '已封存条目', cyberReportTitle: 'AI 代理报告',
  giaAssetsTitle: 'Green IA 标签与证书',
  giaLabelDl: '下载标签 (SVG)',
  giaCertDl: '下载证书（PDF 预览）',
  subscriptionsSectionTitle: '高级订阅',
  photosFlag: '标记',
  photosUnflag: '恢复',
  photosFlagged: '已标记',
  photosFlaggedBadge: '已标记',
  photosEmpty: '暂无共享照片',
  photosConfirmDelete: '删除这个照片帖子？',
  photosDeleted: '照片帖子已删除',
  vtStatsTotal: '视频总数',
  vtStatsFlagged: '被举报',
  vtStatsLikes: '累计点赞',
  vtStatsViews: '累计观看',
  saTitle: '社交媒体 AI 代理',
  saTabCompose: '撰写',
  saTabList: '已排程',
  saTabMod: '审核',
  saTitleField: '标题 *',
  saBodyField: '正文 *',
  saHashtags: '话题标签（例如 #电影 #ai）',
  saMediaUrl: '媒体 URL（图片/视频 — 可选）',
  saSchedule: '排程',
  saMissing: '标题、正文和至少一个平台为必填',
  saScheduled: '已排程',
  saFlagged: '已排程但被标记（检测到禁词）',
  saError: '错误',
  saEmpty: '暂无排程贴文。',
  saDeleteConfirm: '删除此贴文？',
  saModTitle: '自动审核禁词',
  saModDesc: '包含以下任一禁词的贴文将被自动标记并阻止发布。',
  saModAdd: '添加禁词…',
  saModSave: '保存',
  saModSaved: '配置已保存',
  tileAdsValidationTitle: '广告审核',
  tileAdsValidationDesc: '批准待审广告',
  tileMonetizationTitle: '变现',
  tileMonetizationDesc: '赞助商与创作者报价',
  tileAdsTitle: '广告',
  tileAdsDesc: '活跃活动',

  tmdbSyncing: '同步中…',
  tmdbSyncDone: 'TMDB 同步完成',
  tmdbSyncError: 'TMDB 同步错误',

  weeklyCinemaFilms: '精选影片',
  weeklyCinemaAudience: '目标受众',
  weeklyCinemaHint: '每周向用户发送电影精选通知。',
  weeklyCinemaPreview: '预览',
  weeklyCinemaSend: '发送',
  weeklyCinemaSending: '发送中…',
  weeklyCinemaConfirm: '确认发送给所有用户？',

  apiaKeysTitle: 'AP-IA 密钥',
  apiaTabPending: '待处理',
  apiaTabApproved: '已批准',
  apiaTabRefused: '已拒绝',
  apiaRequestsEmpty: '暂无申请',
  apiaSubmittedOn: '提交于',
  apiaPurpose: '用途',
  apiaReason: '原因',
  apiaApprove: '批准',
  apiaRefuse: '拒绝',
  apiaApproved: '已批准',
  apiaRefused: '已拒绝',
  apiaRefusePrompt: '拒绝原因（可选）',
  apiaKeyRevoke: '撤销',
  apiaKeyRevoked: '密钥已撤销',
  apiaManualTitle: '生成手动密钥',
  apiaManualHint: '不通过用户申请流程直接创建 AP-IA 密钥（内部使用）。',
  apiaManualNamePh: '密钥名称',
  apiaManualDescPh: '描述（可选）',
  apiaManualGenerate: '生成密钥',

  teamTabTeam: '团队',
  teamTabPartners: '合伙人',
  teamEmpty: '暂无成员',
  teamPartnersEmpty: '暂无合伙人',
  teamGrant: '添加成员',
  teamGrantConfirm: '将此用户添加到团队？',
  teamGranted: '成员已加入团队',
  teamConfirmRevoke: '将该成员从团队中移除？',
  teamRevoked: '成员已移除',
  teamJobType: '职位',
  teamContract: '合同类型',
  teamHours: '每周工时',
  teamSalaryEmail: '工资联系邮箱',
  teamEntryDate: '入职日期',
  teamCvUpload: '上传简历（PDF）',
  teamCvReplace: '替换简历',
  teamCvView: '查看简历',
  teamCvUploaded: '简历已上传',
  teamPhoto: '照片',
  teamPhotoUpload: '上传照片',
  teamPhotoUploaded: '照片已上传',
  teamProfileSave: '保存资料',
  teamProfileSaved: '资料已保存',
  teamSlip: '工资单',
  teamSlipPdf: '工资单（PDF）',
  teamSlipSend: '发送工资单',
  teamSlipSent: '工资单已发送',
  teamSlipStored: '工资单已归档',
  teamSlipFileRequired: '需要 PDF 文件',
  teamSlipPeriodInvalid: '无效周期（格式 YYYY-MM）',
  teamPartnerAdd: '添加合伙人',
  teamPartnerAdded: '合伙人已添加',
  teamPartnerNew: '新合伙人',
  teamPartnerType: '类型',
  teamPartnerShareholder: '股东',
  teamPartnerConfirmDelete: '删除该合伙人？',

  slipsTab: '工资单',
  slipsEmpty: '暂无工资单',
  slipsDownload: '下载',
  slipsDelete: '删除',
  slipsConfirmDelete: '删除此工资单？',

  usersByLangTitle: '按语言分类的用户',
  usersByLangSubtitle: '在 13 种受支持语言中的分布',
  usersByLangOther: '其他 / 未设置',
  usersByLangTotal: '总计',
  usersByLangEmpty: '暂无数据',
  usersByLangClickHint: '点击查看分布',
};

export const adminSupportedLanguages = ['fr', 'en', 'es', 'zh'];
export const adminI18n = { fr, en, es, zh };
